// The tool templates of a journal card (spec 14 §4.4; K-3's final data,
// D-538 ruling 4). Each renders what its tool's evidence and params carry
// (lib/card-data.ts), and links the record it acts on, so a manager decides
// from the card and can still open the document behind it:
//
//   send_quote          the quote's lines with their price source, the ranked
//                       ways to fill each line, the total against the cap, the
//                       reliability score against its floor, the agent sends
//                       this week, and "No channel can deliver this" on the
//                       forced ask (D-517 ruling 2);
//   draft_quote         the lines the agent drafted, priced for a value
//                       holder, and the quote once it exists (D-516);
//   reply_order_status  the order and a preview of the status card the
//                       customer receives (D-518);
//   send_text           the body in the untrusted frame, and its conversation
//                       (D-520).
//
// Money is printed only where the server served it (spec 14 §7.5): an absent
// price leaves its cell out, never a zero. Every other tool keeps the generic
// card, which shows the tool's name and evidence in the expander.

import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import { UntrustedText } from "../../../../components/untrusted-text";
import { enumLabel } from "../../../../lib/enum-label";
import { formatQuantity } from "../../../../lib/quantity";
import { formatMinor } from "../../inventory/lib/cost";
import { cardTemplateOf, type AgentAction } from "../lib/agents-lib";
import {
  AGENT_CUSTOMER_SEND_LIMIT,
  belowReliability,
  draftQuoteCard,
  orderStatusCard,
  overCap,
  sendQuoteCard,
  sendTextCard,
  type CardAlternative,
  type CardLine,
} from "../lib/card-data";

function money(minor: number | undefined, currency: string | undefined, locale: string) {
  return minor === undefined || currency === undefined
    ? undefined
    : formatMinor(minor, currency, locale);
}

function priceSourceText(t: TFunction, line: CardLine): string | undefined {
  if (line.priceSource === undefined) return undefined;
  // The off-cut source names its discount in the composer; the card has no
  // percentage to print, so it says the source alone.
  return line.priceSource === "offcut_class"
    ? t("sales.priceSource.offcutPlain")
    : enumLabel(t, "sales.priceSource", line.priceSource);
}

/** One line of a quote as the card shows it: what, how much, how priced. */
function Lines({
  lines,
  currency,
  testId,
}: {
  lines: readonly CardLine[];
  currency: string | undefined;
  testId: string;
}) {
  const { t, i18n } = useTranslation();
  if (lines.length === 0) return null;
  return (
    <ul className="ag-lines" data-testid={testId} aria-label={t("agents.journal.template.lines")}>
      {lines.map((line, index) => {
        const source = priceSourceText(t, line);
        const unit = money(line.unitPriceMinor, currency, i18n.language);
        const total = money(line.lineTotalMinor, currency, i18n.language);
        return (
          <li
            key={`${line.lineNo ?? "x"}-${index}`}
            className="ag-line"
            data-testid="action-line"
            data-line-no={line.lineNo}
          >
            <span className="ag-line-what">{line.description}</span>
            <span className="ag-line-qty num">
              {formatQuantity(line.qty, { unit: line.uom, locale: i18n.language })}
            </span>
            {source !== undefined ? (
              <StatusChip variant="neutral" dot={false} data-testid="action-line-source">
                {source}
              </StatusChip>
            ) : null}
            {line.fulfillmentKind !== undefined ? (
              <span className="ag-hint" data-testid="action-line-fill">
                {t(`agents.journal.template.fill.${line.fulfillmentKind}`)}
              </span>
            ) : null}
            {unit !== undefined ? (
              <span className="ag-line-price num" data-testid="action-line-unit">
                {t("agents.journal.template.perUnit", { price: unit })}
              </span>
            ) : null}
            {total !== undefined ? (
              <span className="ag-line-total num" data-testid="action-line-total">
                {total}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The ranked ways to fill each line; the one the draft used is marked. */
function Alternatives({
  alternatives,
  currency,
}: {
  alternatives: readonly CardAlternative[];
  currency: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  if (alternatives.length === 0) return null;
  return (
    <div className="ag-alts" data-testid="action-alternatives">
      <p className="ag-alts-title">{t("agents.journal.template.alternatives")}</p>
      <ol className="ag-lines">
        {alternatives.map((alt) => {
          const total = money(alt.lineTotalMinor, currency, i18n.language);
          return (
            <li
              key={`${alt.lineNo ?? "x"}-${alt.rank}`}
              className="ag-line"
              data-testid="action-alternative"
              data-kind={alt.kind}
              data-chosen={alt.chosen}
            >
              {alt.lineNo !== undefined ? (
                <span className="ag-line-qty num">
                  {t("agents.journal.template.lineNo", { no: alt.lineNo })}
                </span>
              ) : null}
              <span className="ag-line-what">{t(`agents.journal.template.fill.${alt.kind}`)}</span>
              {alt.pieces !== undefined ? (
                <span className="ag-hint num">
                  {t("agents.journal.template.pieces", { count: alt.pieces })}
                </span>
              ) : null}
              {alt.chosen ? (
                <StatusChip variant="accent" dot={false} data-testid="action-alternative-chosen">
                  {t("agents.journal.template.chosen")}
                </StatusChip>
              ) : null}
              {total !== undefined ? <span className="ag-line-total num">{total}</span> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function QuoteLink({ quoteId, quoteNo }: { quoteId: string; quoteNo: string | undefined }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/sales/quotes/$quoteId"
      params={{ quoteId }}
      className="ag-link"
      data-testid="action-template-quote"
    >
      {quoteNo !== undefined
        ? t("agents.journal.template.quoteNo", { no: quoteNo })
        : t("agents.journal.template.openQuote")}
    </Link>
  );
}

function SendQuoteTemplate({ action }: { action: AgentAction }) {
  const { t, i18n } = useTranslation();
  const card = sendQuoteCard(action);
  const total = money(card.totalMinor, card.currency, i18n.language);
  const cap = money(card.capMinor, card.capCurrency, i18n.language);
  return (
    <div className="ag-template" data-testid="action-template" data-template="send_quote">
      <div className="ag-row">
        {card.quoteId !== undefined ? (
          <QuoteLink quoteId={card.quoteId} quoteNo={card.quoteNo} />
        ) : null}
        {card.noChannel ? (
          <StatusChip variant="warn" data-testid="action-no-channel">
            {t("agents.journal.template.noChannel")}
          </StatusChip>
        ) : null}
        {card.marginBreached ? (
          <StatusChip variant="warn" data-testid="action-margin-breached">
            {t("agents.journal.template.marginBreached")}
          </StatusChip>
        ) : null}
        {card.offListLines !== undefined && card.offListLines > 0 ? (
          <StatusChip variant="bad" data-testid="action-off-list">
            {t("agents.journal.template.offList", { count: card.offListLines })}
          </StatusChip>
        ) : null}
      </div>
      <Lines lines={card.lines} currency={card.currency} testId="action-lines" />
      <Alternatives alternatives={card.alternatives} currency={card.currency} />
      <dl className="ag-detail-list" data-testid="action-send-facts">
        {total !== undefined ? (
          <div style={{ display: "contents" }}>
            <dt>{t("agents.journal.template.total")}</dt>
            <dd className="num" data-testid="action-total" data-over-cap={overCap(card)}>
              {cap !== undefined ? t("agents.journal.template.totalOfCap", { total, cap }) : total}
            </dd>
          </div>
        ) : null}
        {card.reliabilityScore !== undefined ? (
          <div style={{ display: "contents" }}>
            <dt>{t("agents.journal.template.reliability")}</dt>
            <dd
              className="num"
              data-testid="action-reliability"
              data-below={belowReliability(card)}
            >
              {card.reliabilityScore === null
                ? t("agents.journal.template.reliabilityUnknown")
                : typeof card.minReliability === "number"
                  ? t("agents.journal.template.reliabilityOf", {
                      score: card.reliabilityScore.toLocaleString(i18n.language),
                      min: card.minReliability.toLocaleString(i18n.language),
                    })
                  : card.reliabilityScore.toLocaleString(i18n.language)}
            </dd>
          </div>
        ) : null}
        {card.sends7d !== undefined ? (
          <div style={{ display: "contents" }}>
            <dt>{t("agents.journal.template.sends")}</dt>
            <dd className="num" data-testid="action-sends">
              {t("agents.journal.template.sendsOf", {
                count: card.sends7d,
                limit: AGENT_CUSTOMER_SEND_LIMIT,
              })}
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

function DraftQuoteTemplate({ action }: { action: AgentAction }) {
  const { t, i18n } = useTranslation();
  const card = draftQuoteCard(action);
  const total = money(card.totalMinor, card.currency, i18n.language);
  return (
    <div className="ag-template" data-testid="action-template" data-template="draft_quote">
      {card.quoteId !== undefined ? (
        <div className="ag-row">
          <QuoteLink quoteId={card.quoteId} quoteNo={undefined} />
        </div>
      ) : null}
      {card.lines.length > 0 ? (
        <Lines lines={card.lines} currency={card.currency} testId="action-lines" />
      ) : card.requestedLines > 0 ? (
        <p className="ag-hint" data-testid="action-lines-count">
          {t("agents.journal.template.linesCount", { count: card.requestedLines })}
        </p>
      ) : null}
      {total !== undefined ? (
        <p className="ag-row">
          <span>{t("agents.journal.template.total")}</span>
          <span className="num" data-testid="action-total">
            {total}
          </span>
        </p>
      ) : null}
    </div>
  );
}

function OrderStatusTemplate({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  const card = orderStatusCard(action);
  if (card.orderId === undefined) return null;
  return (
    <div className="ag-template" data-testid="action-template" data-template="reply_order_status">
      <div className="ag-row">
        <Link
          to="/sales/orders/$orderId"
          params={{ orderId: card.orderId }}
          className="ag-link"
          data-testid="action-template-order"
        >
          {card.orderNo !== undefined
            ? t("agents.journal.template.orderNo", { no: card.orderNo })
            : t("agents.journal.template.openOrder")}
        </Link>
      </div>
      {card.orderNo !== undefined && card.status !== undefined ? (
        // The status card the customer gets is module 17's own template; the
        // preview says what it will name, in the reader's language.
        <p className="ag-preview" data-testid="action-status-preview">
          <span className="ag-preview-label">{t("agents.journal.template.customerGets")}</span>{" "}
          {t("agents.journal.template.statusPreview", {
            no: card.orderNo,
            status: enumLabel(t, "sales.orders.status", card.status),
          })}
        </p>
      ) : null}
    </div>
  );
}

function SendTextTemplate({ action }: { action: AgentAction }) {
  const { t } = useTranslation();
  const card = sendTextCard(action);
  if (card.body === undefined && card.conversationId === undefined) return null;
  return (
    <div className="ag-template" data-testid="action-template" data-template="send_text">
      {card.body !== undefined ? (
        <UntrustedText
          label={t("agents.journal.template.body")}
          frameNote={t("agents.journal.note.frame")}
          text={card.body}
          data-testid="action-template-body"
        />
      ) : null}
      {card.conversationId !== undefined ? (
        <Link
          to="/messaging/inbox"
          search={{ tab: "all", c: card.conversationId }}
          className="ag-link"
          data-testid="action-template-conversation"
        >
          {t("agents.journal.template.openConversation")}
        </Link>
      ) : null}
    </div>
  );
}

/** The tool's template (spec 14 §4.4); the generic card renders none. */
export function Template({ action }: { action: AgentAction }) {
  switch (cardTemplateOf(action.tool_code)) {
    case "send_quote":
      return <SendQuoteTemplate action={action} />;
    case "draft_quote":
      return <DraftQuoteTemplate action={action} />;
    case "reply_order_status":
      return <OrderStatusTemplate action={action} />;
    case "send_text":
      return <SendTextTemplate action={action} />;
    default:
      return null;
  }
}
